import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, FolderInput, MoreVertical, Pencil, Share2, Trash2, Eye } from "lucide-react";
import { api, CATEGORY_LABELS, errMsg, fmtBytes, fmtDate } from "@/lib/api";
import { useFiles } from "@/context/FilesContext";
import { FileIcon } from "@/components/files/FileIcon";
import { NameDialog } from "@/components/files/NameDialog";
import { MoveDialog } from "@/components/files/MoveDialog";
import { PermissionsDialog } from "@/components/files/PermissionsDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

function ItemMenu({ item, isFolder, onAction }) {
  const canEdit = ["edit", "owner"].includes(item.access);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label="Actions" data-testid={`item-menu-${item.id}`}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => onAction("open")} data-testid="action-open"><Eye className="h-4 w-4 mr-2" />Ouvrir</DropdownMenuItem>
        {canEdit && <DropdownMenuItem onClick={() => onAction("rename")} data-testid="action-rename"><Pencil className="h-4 w-4 mr-2" />Renommer</DropdownMenuItem>}
        {canEdit && <DropdownMenuItem onClick={() => onAction("move")} data-testid="action-move"><FolderInput className="h-4 w-4 mr-2" />Déplacer</DropdownMenuItem>}
        {!isFolder && <DropdownMenuItem onClick={() => onAction("copy")} data-testid="action-copy"><Copy className="h-4 w-4 mr-2" />Copier vers…</DropdownMenuItem>}
        {item.access === "owner" && <DropdownMenuItem onClick={() => onAction("share")} data-testid="action-share"><Share2 className="h-4 w-4 mr-2" />Partager et autorisations</DropdownMenuItem>}
        {canEdit && <><DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onAction("delete")} className="text-red-600" data-testid="action-delete"><Trash2 className="h-4 w-4 mr-2" />Mettre à la corbeille</DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const Shared = ({ item }) => (item.permissions?.length ? <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Partagé</span> : null);

export function FileBrowser({ folders = [], files = [], view = "list", onChanged, emptyText = "Ce dossier est vide", showOwner = false }) {
  const navigate = useNavigate();
  const { openFile, bump } = useFiles();
  const [dialog, setDialog] = useState(null);
  const changed = () => { onChanged?.(); bump(); };

  const act = async (item, isFolder, action) => {
    if (action === "open") return isFolder ? navigate(`/fichiers/${item.id}`) : openFile(item.id);
    if (action === "delete") {
      try {
        await api.delete(isFolder ? `/folders/${item.id}` : `/files/${item.id}`);
        toast.success("Déplacé dans la corbeille"); changed();
      } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    setDialog({ action, item, isFolder });
  };

  const run = (fn, ok) => async (...a) => {
    try { await fn(...a); toast.success(ok); changed(); } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const d = dialog;
  const base = d ? (d.isFolder ? `/folders/${d.item.id}` : `/files/${d.item.id}`) : "";

  if (!folders.length && !files.length) {
    return <div className="border border-dashed rounded-xl py-16 text-center text-sm text-slate-500" data-testid="browser-empty">{emptyText}</div>;
  }

  return (
    <>
      {view === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="browser-grid">
          {[...folders.map((f) => [f, true]), ...files.map((f) => [f, false])].map(([it, isF]) => (
            <div key={it.id} onClick={() => act(it, isF, "open")} data-testid={`${isF ? "folder" : "file"}-item-${it.id}`}
              className="group bg-white border rounded-xl p-3 cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-blue-500/30 transition-[transform,box-shadow,border-color] duration-150">
              <div className="flex items-start justify-between">
                <FileIcon file={it} folder={isF} size="lg" />
                <ItemMenu item={it} isFolder={isF} onAction={(a) => act(it, isF, a)} />
              </div>
              <div className="mt-3 text-sm font-medium truncate" title={it.name}>{it.name}</div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
                {isF ? "Dossier" : fmtBytes(it.size)} <Shared item={it} />
                {showOwner && it.owner_name && <span className="truncate">· {it.owner_name}</span>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border rounded-xl overflow-hidden" data-testid="browser-list">
          <div className="hidden md:grid grid-cols-[1fr_140px_140px_100px_44px] gap-4 px-4 py-2.5 text-xs uppercase tracking-wider font-semibold text-slate-500 border-b bg-slate-50">
            <span>Nom</span><span>Catégorie</span><span>Modifié</span><span>Taille</span><span />
          </div>
          {[...folders.map((f) => [f, true]), ...files.map((f) => [f, false])].map(([it, isF]) => (
            <div key={it.id} onClick={() => act(it, isF, "open")} data-testid={`${isF ? "folder" : "file"}-item-${it.id}`}
              className="grid grid-cols-[1fr_44px] md:grid-cols-[1fr_140px_140px_100px_44px] gap-4 items-center px-4 py-2.5 border-b last:border-0 hover:bg-blue-50/40 cursor-pointer transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <FileIcon file={it} folder={isF} />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">{it.name} <Shared item={it} />
                    {showOwner && it.owner_name && <span className="text-[11px] font-normal text-slate-500 bg-slate-100 rounded px-1.5" data-testid={`owner-badge-${it.id}`}>{it.owner_name}</span>}</div>
                  <div className="text-xs text-slate-500 md:hidden">{isF ? "Dossier" : `${fmtBytes(it.size)} · ${fmtDate(it.updated_at)}`}</div>
                </div>
              </div>
              <span className="hidden md:block text-xs text-slate-600">{isF ? "Dossier" : (CATEGORY_LABELS[it.category] || (it.ai_status === "pending" ? "Analyse…" : "—"))}</span>
              <span className="hidden md:block text-xs text-slate-600">{fmtDate(it.updated_at)}</span>
              <span className="hidden md:block text-xs text-slate-600 font-mono">{isF ? "—" : fmtBytes(it.size)}</span>
              <ItemMenu item={it} isFolder={isF} onAction={(a) => act(it, isF, a)} />
            </div>
          ))}
        </div>
      )}
      <NameDialog open={d?.action === "rename"} onOpenChange={() => setDialog(null)} title="Renommer" initial={d?.item.name}
        testId="rename-dialog" onSubmit={run((name) => api.patch(base, { name }), "Renommé")} />
      <MoveDialog open={d?.action === "move" || d?.action === "copy"} onOpenChange={() => setDialog(null)}
        title={d?.action === "copy" ? "Copier vers…" : "Déplacer vers…"} excludeId={d?.isFolder ? d.item.id : undefined}
        allowCompanies={d?.action === "move"}
        onSubmit={run((target, companyId) => d.action === "copy"
          ? api.post(`/files/${d.item.id}/copy`, { folder_id: target })
          : api.patch(base, d.isFolder ? { parent_id: target, move: true, company_id: companyId }
            : { folder_id: target, move: true, company_id: companyId }),
          d?.action === "copy" ? "Copie créée" : "Déplacé")} />
      <PermissionsDialog open={d?.action === "share"} onOpenChange={() => setDialog(null)} item={d?.item}
        kind={d?.isFolder ? "folders" : "files"} onSaved={changed} />
    </>
  );
}
